import { useEditor } from '../store/editor'
import { importFromVaideo, uploadMedia } from './api'
import { projectFromVaideo } from './vaideo'
import { saveNow, setUrlProject, writeLocal } from './persist'
import { newProject } from './model'
import type { Asset } from '../types'
import { track } from './analytics'

export async function importVaideoProject(uid: string, opts: { confirmReplace?: boolean } = {}) {
  const st = useEditor.getState()
  if (!st.user) {
    st.toast('Inicia sesión en Vaideo para abrir tus vídeos en el editor', 'error')
    return
  }
  if (opts.confirmReplace && st.project.clips.length && st.dirty &&
      !window.confirm('Se abrirá el vídeo de Vaideo como proyecto nuevo. Los cambios sin guardar de este proyecto se perderán. ¿Continuar?')) {
    return
  }
  st.set({ busy: 'Preparando tu vídeo de Vaideo… La primera vez puede tardar un par de minutos.' })
  try {
    const r = await importFromVaideo(uid)
    const p = projectFromVaideo(r, uid)
    useEditor.getState().setProject(p, { serverId: null })
    setUrlProject(null)
    writeLocal()
    useEditor.getState().set({ busy: null })
    useEditor.getState().toast('Vídeo importado. Subtítulos en su propia pista, listos para editar.', 'ok')
    track('Editor Import', { format: p.origin?.aspectRatio ?? 'unknown' })
    await saveNow({ silent: true })
  } catch (e) {
    useEditor.getState().set({ busy: null })
    useEditor.getState().toast(`No se pudo importar el vídeo: ${(e as Error).message}`, 'error')
  }
}

export function startNewProject() {
  const st = useEditor.getState()
  if (st.project.clips.length && st.dirty && !window.confirm('¿Empezar un proyecto nuevo? Los cambios sin guardar se perderán.')) return
  const p = newProject(st.project.settings.width, st.project.settings.height)
  st.setProject(p, { serverId: null })
  setUrlProject(null)
  writeLocal()
}

export interface UploadItem { key: string; name: string; progress: number; abort: () => void }
type UploadListener = (items: UploadItem[]) => void
let uploads: UploadItem[] = []
const uploadListeners = new Set<UploadListener>()
function emitUploads() { for (const l of uploadListeners) l(uploads) }
export function onUploads(l: UploadListener) { uploadListeners.add(l); l(uploads); return () => { uploadListeners.delete(l) } }

const ACCEPT = /^(video|audio|image)\//

/** Upload files into the library; optionally drop each onto the timeline. */
export async function uploadFiles(files: File[], place?: { trackId?: string; startSec?: number }) {
  const st = useEditor.getState()
  if (!st.user) {
    st.toast('Inicia sesión en Vaideo (gratis) para subir archivos', 'error')
    return
  }
  let at = place?.startSec
  for (const f of files) {
    if (!ACCEPT.test(f.type) && !/\.(mp4|mov|webm|mkv|mp3|wav|m4a|aac|ogg|png|jpe?g|webp|gif)$/i.test(f.name)) {
      st.toast(`${f.name}: formato no soportado`, 'error')
      continue
    }
    const key = `${f.name}-${Date.now()}-${Math.random()}`
    const up = uploadMedia(f, (pr) => {
      uploads = uploads.map((u) => (u.key === key ? { ...u, progress: pr } : u))
      emitUploads()
    })
    uploads = [...uploads, { key, name: f.name, progress: 0, abort: up.abort }]
    emitUploads()
    try {
      const r = await up.promise
      const asset: Asset = { id: r.id, name: f.name, info: r.info, source: 'upload' }
      useEditor.getState().addAsset(asset)
      if (place) {
        useEditor.getState().addAssetClip(asset.id, { trackId: place.trackId, startSec: at })
        if (at !== undefined) {
          const dur = asset.info.kind === 'image' ? 5 : asset.info.durationSec
          at += dur
        }
      }
    } catch (e) {
      useEditor.getState().toast(`${f.name}: ${(e as Error).message}`, 'error')
    } finally {
      uploads = uploads.filter((u) => u.key !== key)
      emitUploads()
    }
  }
}
